/**
 * Tela de login de staff (Task 10).
 *
 * Existe porque o provedor real do Supabase precisa de um caminho de
 * **aquisição** do token: sem esta tela, uma build configurada chegaria ao
 * guard sem token e negaria sempre. Ela só aparece quando o provedor suporta
 * login (`supportsLogin`) e não há sessão ativa.
 *
 * O componente é "burro" de propósito: não fala com rede nem guarda token —
 * recebe `onSubmit` do `StaffSessionBoundary`, que chama
 * `provider.signIn(...)`. Assim o fluxo de credencial fica num lugar só.
 *
 * Acessibilidade: cada campo tem `label` associado, o erro é anunciado por
 * `role="alert"` e referenciado por `aria-describedby`, e o botão desabilita
 * durante o envio para não duplicar tentativa.
 */
import { createElement as h, useState, type FormEvent, type ReactElement } from "react";
import { greetingFor } from "./greeting.js";

export interface StaffLoginProps {
  onSubmit: (email: string, password: string) => void;
  pending: boolean;
  error: string | null;
  /** Mostrado como contexto do ambiente (ex.: projeto Supabase configurado). */
  hint?: string;
}

/**
 * Suporte da operação pelo WhatsApp. Formato wa.me: DDI 55 + DDD 41 + número
 * informado pelo time (40638879). Se o número mudar (ex.: ganhar o 9º dígito),
 * este é o único ponto a ajustar.
 */
export const SUPPORT_WHATSAPP_URL = "https://wa.me/554140638879";

/**
 * Módulos exibidos na "constelação" do palco de marca. Passar o mouse (ou focar/
 * tocar) num módulo acende a ligação dele com o hub e mostra a descrição.
 * `left`/`top` são percentuais da área da rede; as mesmas coordenadas (em 0–100)
 * alimentam as linhas do SVG, então mudar uma posição exige mudar as duas.
 */
const MODULES = [
  {
    id: "atendimento",
    label: "Atendimento",
    detail: "Conversas de clientes, tags e histórico em um só lugar.",
    left: 22,
    top: 18,
    icon: ["M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z"]
  },
  {
    id: "pedidos",
    label: "Pedidos",
    detail: "Pedidos de todos os canais, do pagamento à entrega.",
    left: 78,
    top: 18,
    icon: ["M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z", "M3 6h18", "M16 10a4 4 0 0 1-8 0"]
  },
  {
    id: "expedicao",
    label: "Expedição",
    detail: "Separação, etiqueta e envio sem sair do hub.",
    left: 22,
    top: 82,
    icon: ["M21 8 12 3 3 8v8l9 5 9-5z", "m3 8 9 5 9-5", "M12 13v8"]
  },
  {
    id: "painel",
    label: "Painel",
    detail: "Receita e indicadores da operação num relance.",
    left: 78,
    top: 82,
    icon: ["M3 3v18h18", "m7 15 4-4 3 3 5-6"]
  }
] as const;

const NETWORK_IDLE_TEXT = "Passe o mouse (ou toque) em um módulo para ver como tudo se conecta.";

/** Ícone de traço no padrão do app (decorativo: aria-hidden). */
function strokeIcon(paths: readonly string[], size: number, extra = ""): ReactElement {
  return h(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": "true",
      className: extra
    },
    ...paths.map((d) => h("path", { key: d, d }))
  );
}

export function StaffLogin({ onSubmit, pending, error, hint }: StaffLoginProps): ReactElement {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  // Calculada uma vez ao montar: a saudação não deve "piscar" trocando de frase
  // enquanto a pessoa digita a senha.
  const [greeting] = useState(() => greetingFor(new Date()));
  const activeModule = MODULES.find((module) => module.id === active) ?? null;
  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (pending) return;
    onSubmit(email.trim(), password);
  };
  // Redesign R6 (apresentação apenas): cartão centralizado tokenizado no padrão
  // aprovado (ver ProdutoDetalhe/StatusPanel). Os campos usam label associado
  // (htmlFor/id preservados), o erro é ligado por aria-describedby e anunciado
  // por role="alert", e o CTA primário é o vermelho de marca único. Os nomes de
  // classe hook (.staff-login*) seguem no markup — o CSS legado foi removido de
  // styles.css, então não há regra concorrendo. data-page/ids/role/texto e o
  // comportamento (desabilitar no envio) são PRESERVADOS (os testes batem neles).
  //
  // Redesign "Painel de comando" (login do hub): tela dividida. À esquerda, o
  // palco de marca (sempre escuro, nos dois temas) com logo, mensagem e a
  // constelação de módulos conectados; à direita, o formulário sobre os tokens
  // do tema. As regras de animação/gradiente do palco moram em styles.css
  // (bloco "Login de staff"), porque Tailwind não expressa keyframes e máscaras.
  // O movimento respeita prefers-reduced-motion pela regra global de tailwind.css.
  const fieldClass = "staff-login__field flex flex-col gap-2";
  const inputClass =
    "h-12 w-full rounded-control border border-border-strong bg-surface px-4 text-base text-ink " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus";
  const labelClass = "text-sm font-bold text-body";

  const stage = h(
    "div",
    { className: "staff-login__stage flex min-h-[520px] min-w-0 flex-[1_1_560px] flex-col justify-between gap-8 p-8 sm:p-12" },
    h("span", { className: "staff-login__glow", "aria-hidden": "true" }),
    h("span", { className: "staff-login__glow staff-login__glow--low", "aria-hidden": "true" }),
    h("span", { className: "staff-login__dots", "aria-hidden": "true" }),
    h(
      "div",
      { className: "relative" },
      h("img", {
        src: "/super2-logo-light.webp",
        alt: "Super2 Eletrônicos",
        width: 150,
        height: 60,
        className: "staff-login__logo h-[60px] w-[150px]"
      })
    ),
    h(
      "div",
      { className: "relative flex max-w-[520px] flex-col gap-4" },
      h("p", { className: "text-xs font-bold uppercase tracking-[0.14em] text-[#ff8a8f]" }, "Novo momento. Mesmo time."),
      h(
        "p",
        { className: "text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl" },
        "Todo o Super2, em um só lugar."
      ),
      h(
        "p",
        { className: "text-base leading-relaxed text-[#c4c9d4]" },
        "Atendimento, pedidos e expedição conversando entre si, para você resolver mais com menos cliques."
      )
    ),
    // Constelação: hub central ligado a cada módulo. Coordenadas 0–100 espelham
    // os `left`/`top` dos botões (ver MODULES). non-scaling-stroke mantém a
    // espessura da linha mesmo com o SVG esticado (preserveAspectRatio="none").
    h(
      "div",
      { className: "relative flex max-w-[520px] flex-col gap-3" },
      h(
        "div",
        { className: "staff-login__net relative h-[230px] w-full" },
        h(
          "svg",
          {
            className: "absolute inset-0 h-full w-full",
            viewBox: "0 0 100 100",
            preserveAspectRatio: "none",
            "aria-hidden": "true"
          },
          ...MODULES.map((module) =>
            h("line", {
              key: module.id,
              x1: 50,
              y1: 50,
              x2: module.left,
              y2: module.top,
              vectorEffect: "non-scaling-stroke",
              className: "staff-login__link" + (active === module.id ? " is-active" : "")
            })
          )
        ),
        h("span", { className: "staff-login__hub", style: { left: "50%", top: "50%" }, "aria-hidden": "true" }),
        ...MODULES.map((module) =>
          h(
            "button",
            {
              key: module.id,
              type: "button",
              "aria-pressed": active === module.id,
              className: "staff-login__chip" + (active === module.id ? " is-active" : ""),
              style: { left: `${module.left}%`, top: `${module.top}%` },
              onMouseEnter: () => setActive(module.id),
              onMouseLeave: () => setActive(null),
              onFocus: () => setActive(module.id),
              onBlur: () => setActive(null),
              onClick: () => setActive(active === module.id ? null : module.id)
            },
            strokeIcon(module.icon, 16, "shrink-0 text-[#ff8a8f]"),
            module.label
          )
        )
      ),
      h(
        "p",
        { className: "min-h-[3rem] text-sm leading-relaxed text-[#c4c9d4]", "aria-live": "polite" },
        activeModule ? `${activeModule.label}: ${activeModule.detail}` : NETWORK_IDLE_TEXT
      )
    )
  );

  const formPanel = h(
    "div",
    { className: "flex min-w-0 flex-[1_1_480px] items-center justify-center bg-canvas p-8" },
    h(
      "div",
      { className: "flex w-full max-w-[400px] flex-col gap-7" },
      h(
        "div",
        { className: "flex flex-col gap-2" },
        h("p", { className: "staff-login__greeting text-sm font-bold text-brand-text" }, greeting),
        h("h2", { id: "staff-login-title", className: "text-3xl font-extrabold tracking-tight text-ink" }, "Entrar no Hub"),
        h(
          "p",
          { className: "staff-login__note text-base leading-relaxed text-muted" },
          hint ?? "Acesso restrito à operação: apenas contas com papel operator ou admin."
        )
      ),
      error
        ? h(
            "p",
            {
              className: "staff-login__error rounded-control border border-danger bg-surface px-3 py-2 text-sm font-semibold text-danger",
              id: "staff-login-error",
              role: "alert"
            },
            error
          )
        : null,
      h(
        "form",
        {
          className: "staff-login__form flex flex-col gap-5",
          onSubmit: handleSubmit,
          "aria-describedby": error ? "staff-login-error" : undefined
        },
        h(
          "p",
          { className: fieldClass },
          h("label", { htmlFor: "staff-login-email", className: labelClass }, "E-mail de operação"),
          h("input", {
            id: "staff-login-email",
            name: "email",
            type: "email",
            autoComplete: "username",
            required: true,
            className: inputClass,
            value: email,
            onChange: (event: { target: { value: string } }) => setEmail(event.target.value)
          })
        ),
        h(
          "p",
          { className: fieldClass },
          h("label", { htmlFor: "staff-login-password", className: labelClass }, "Senha"),
          h(
            "span",
            { className: "relative block" },
            h("input", {
              id: "staff-login-password",
              name: "password",
              type: showPassword ? "text" : "password",
              autoComplete: "current-password",
              required: true,
              className: inputClass + " pr-12",
              value: password,
              onChange: (event: { target: { value: string } }) => setPassword(event.target.value)
            }),
            h(
              "button",
              {
                type: "button",
                "aria-label": showPassword ? "Ocultar senha" : "Mostrar senha",
                "aria-pressed": showPassword,
                className:
                  "staff-login__eye absolute right-1.5 top-1.5 flex h-9 w-9 items-center justify-center rounded-control " +
                  "text-muted hover:bg-surface-soft",
                onClick: () => setShowPassword(!showPassword)
              },
              strokeIcon(["M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z", "M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"], 20)
            )
          )
        ),
        h(
          "button",
          {
            type: "submit",
            className:
              "staff-login__submit inline-flex h-[54px] items-center justify-center rounded-control bg-brand px-4 " +
              "text-base font-bold text-on-brand hover:bg-brand-pressed disabled:opacity-70",
            disabled: pending
          },
          pending ? "Entrando…" : "Entrar"
        )
      ),
      h(
        "div",
        { className: "staff-login__footer flex items-center justify-between gap-3 border-t border-border pt-5 text-sm text-muted" },
        h(
          "span",
          { className: "flex items-center gap-2" },
          h("span", { className: "h-2 w-2 shrink-0 rounded-full bg-success", "aria-hidden": "true" }),
          "Sistemas operando normalmente"
        ),
        h(
          "a",
          {
            href: SUPPORT_WHATSAPP_URL,
            target: "_blank",
            rel: "noopener noreferrer",
            "aria-label": "Suporte pelo WhatsApp (abre em nova aba)",
            className: "staff-login__support inline-flex items-center gap-1.5 font-bold text-brand-text hover:underline"
          },
          strokeIcon(["M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z"], 16),
          "Suporte"
        )
      )
    )
  );

  return h(
    "section",
    {
      className: "staff-login flex min-h-screen flex-wrap bg-canvas",
      "data-page": "login",
      "aria-labelledby": "staff-login-title"
    },
    stage,
    formPanel
  );
}
